import React, { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "styled-components";
import { theme } from "./styles/theme";
import { GlobalStyle } from "./styles/globalStyles";
import PageLayout from "./components/layout/PageLayout";
import HomePage from "./pages/HomePage";
import CouponsPage from "./pages/CouponsPage";
import StoresPage from "./pages/StoresPage";
import StorePage from "./pages/StorePage";
import CouponDetailPage from "./pages/CouponDetailPage";
import CategoriesPage from "./pages/CategoriesPage";
import CategoryDetailPage from "./pages/CategoryDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import SubmitDealPage from "./pages/SubmitDealPage";
import SignInPage from "./pages/SignInPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import NotFoundPage from "./pages/NotFoundPage";

// SEO & i18n
import SEOVerificationTags from "./components/common/SEOVerificationTags";
import GoogleAnalytics from "./components/common/GoogleAnalytics";
import LanguageRouteWrapper from "./components/common/LanguageRouteWrapper";
import LanguageRoute from "./components/common/LanguageRoute";
import { setVisitorCountry } from "./store/actions/visitorActions";
import apiConfig from "./config/apiConfig";
import ProtectedRoute from "./components/common/ProtectedRoute";
import LegacyUrlRedirect from "./components/common/LegacyUrlRedirect";

// GEO visitor tracker
const VisitorTracker = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const trackVisitor = async () => {
      try {
        const response = await fetch(`${apiConfig.BASE_URL}/geo/ip`);
        if (!response.ok) return;
        const data = await response.json();
        const countryName = data.country_name || data.country || "";
        const rawCode = data.country_code || data.countryCode || "";
        const countryCode =
          typeof rawCode === "string" && /^[a-z]{2}$/i.test(rawCode.trim())
            ? rawCode.trim().toUpperCase()
            : "";
        if (countryName || countryCode) {
          dispatch(
            setVisitorCountry({
              countryName: countryName || undefined,
              countryCode: countryCode || undefined,
            })
          );
          if (countryName) localStorage.setItem("visitorCountry", countryName);
          if (countryCode) localStorage.setItem("visitorCountryCode", countryCode);
        }
      } catch {
        // silently fail – geo is non-critical
      }
    };
    trackVisitor();
  }, [dispatch, location]);

  return null;
};

// Inner app that uses translation initialization
const AppInner = () => {
  useLayoutEffect(() => {
    document.body.classList.add("seo-prerender-ready");
    return () => document.body.classList.remove("seo-prerender-ready");
  }, []);

  return (
    <LanguageRouteWrapper>
      <SEOVerificationTags />
      <GoogleAnalytics />
      <LegacyUrlRedirect />
      <VisitorTracker />
      <Routes>
        {/* Default routes */}
        <Route path="/" element={<PageLayout />}>
          <Route index element={<HomePage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="stores" element={<StoresPage />} />
          <Route path="store/:slug" element={<StorePage />} />
          <Route path="coupon/:id" element={<CouponDetailPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="category/:slug" element={<CategoryDetailPage />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="signin" element={<SignInPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="submit-deal" element={<SubmitDealPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Language-prefixed routes (/:lang/...) */}
        <Route path="/:lang" element={<LanguageRoute><PageLayout /></LanguageRoute>}>
          <Route index element={<HomePage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="stores" element={<StoresPage />} />
          <Route path="store/:slug" element={<StorePage />} />
          <Route path="coupon/:id" element={<CouponDetailPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="category/:slug" element={<CategoryDetailPage />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="signin" element={<SignInPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="submit-deal" element={<SubmitDealPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
          </Route>
        </Route>
      </Routes>
    </LanguageRouteWrapper>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
